import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

export class AttendanceSystem {
  constructor() {
    this.attendanceRecords = new Map();
    this.currentDate = new Date().toISOString().split('T')[0];
    this.employees = [];
  }

  initializeEmployees(employeeList) {
    this.employees = employeeList || [];
    this.employees.forEach(emp => {
      if (!this.attendanceRecords.has(emp.id)) {
        this.attendanceRecords.set(emp.id, new Map());
      }
    });
  }

  recordAttendance(employeeId) {
    const today = new Date().toISOString().split('T')[0];
    const employeeRecord = this.attendanceRecords.get(employeeId);
    if (employeeRecord.has(today)) {
      return { success: false, message: "Attendance already recorded for today." };
    }
    employeeRecord.set(today, { status: 'present', timestamp: new Date() });
    return { success: true, message: "Attendance recorded successfully." };
  }

  checkAttendance(employeeId) {
    const today = new Date().toISOString().split('T')[0];
    const employeeRecord = this.attendanceRecords.get(employeeId);
    return employeeRecord?.get(today)?.status || 'absent';
  }

  markAbsentAtEndOfDay() {
    const today = new Date().toISOString().split('T')[0];
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    if (new Date() >= endOfDay) {
      this.employees.forEach(emp => {
        const record = this.attendanceRecords.get(emp.id);
        if (!record.has(today)) {
          record.set(today, { status: 'absent', timestamp: endOfDay });
        }
      });
      this.currentDate = new Date().toISOString().split('T')[0];
    }
  }

  getAttendanceStats(employeeId, period) {
    const now = new Date();
    const stats = { present: 0, absent: 0 };
    const records = this.attendanceRecords.get(employeeId);
    records?.forEach((record, date) => {
      const recordDate = new Date(date);
      let isWithinPeriod = false;
      if (period === 'week') {
        isWithinPeriod = now.toLocaleDateString() === recordDate.toLocaleDateString() ||
                         now.getDate() - recordDate.getDate() <= 7;
      } else if (period === 'month') {
        isWithinPeriod = now.getMonth() === recordDate.getMonth() &&
                         now.getFullYear() === recordDate.getFullYear();
      }
      if (isWithinPeriod) {
        if (record.status === 'present') stats.present++;
        else if (record.status === 'absent') stats.absent++;
      }
    });
    return stats;
  }

  getAttendanceHistory(employeeId) {
    const records = this.attendanceRecords.get(employeeId) || new Map();
    return Array.from(records.entries()).map(([date, { status, timestamp }]) => ({
      date,
      status,
      time: timestamp.toLocaleTimeString(),
    }));
  }
}

const EmployerAttendance = ({ employeeList = [] }) => {
  const [attendanceSystem] = useState(() => new AttendanceSystem());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [attendanceHistory, setAttendanceHistory] = useState([]);

  useEffect(() => {
    attendanceSystem.initializeEmployees(employeeList);
    if (selectedEmployeeId) {
      setAttendanceHistory(attendanceSystem.getAttendanceHistory(selectedEmployeeId));
    }
  }, [attendanceSystem, employeeList, selectedEmployeeId]);

  const handleAttendance = (employeeId) => {
    const result = attendanceSystem.recordAttendance(employeeId);
    toast(result.success ? result.message : `${result.message} (Employee: ${employeeList.find(emp => emp.id === employeeId)?.name})`, {
      type: result.success ? 'success' : 'warning',
    });
    if (result.success) {
      setSelectedEmployeeId(employeeId);
      setAttendanceHistory(attendanceSystem.getAttendanceHistory(employeeId));
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <h3 className="text-xl font-semibold mb-4">Mark Attendance</h3>
      <select
        value={selectedEmployeeId}
        onChange={(e) => {
          const employeeId = e.target.value;
          setSelectedEmployeeId(employeeId);
          setAttendanceHistory(attendanceSystem.getAttendanceHistory(employeeId));
        }}
        className="w-full p-2 border rounded mb-4"
      >
        <option value="">Select Employee</option>
        {employeeList.length > 0 ? (
          employeeList.map(emp => (
            <option key={emp.id} value={emp.id}>
              {emp.name || `Employee ${emp.id.slice(-4)}`}
            </option>
          ))
        ) : (
          <option disabled>No employees available</option>
        )}
      </select>
      <button
        onClick={() => handleAttendance(selectedEmployeeId)}
        disabled={!selectedEmployeeId}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Mark Present
      </button>
      {attendanceHistory.length > 0 && (
        <div className="mt-4">
          <h4 className="text-lg font-semibold mb-2">Attendance History</h4>
          <table className="w-full text-sm md:text-base">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 text-left">Date</th>
                <th className="py-2 px-4 text-left">Status</th>
                <th className="py-2 px-4 text-left">Time</th>
              </tr>
            </thead>
            <tbody>
              {attendanceHistory.map((record, index) => (
                <tr key={index} className="border-t">
                  <td className="py-2 px-4">{record.date}</td>
                  <td className="py-2 px-4">{record.status}</td>
                  <td className="py-2 px-4">{record.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployerAttendance;
















































// import React, { useState, useEffect } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { fetchEmployees, addEmployee, addSalary, updateEmployee, deleteEmployee, approveLeave, rejectLeave } from '../../redux/employer-employeeSlice/EmployerEmployeeSlice';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { BiPlus, BiCheck, BiX, BiEdit, BiTrash, BiDownload, BiDollar, BiSearch } from 'react-icons/bi';
// import Chart from 'chart.js/auto';
// import EmployerAttendance from './EmployerAttendance';

// const EmployerEmployee = () => {
//   const dispatch = useDispatch();
//   const { employees, status } = useSelector(state => state.employerEmployee);
//   const { currentUser } = useSelector(state => state.user);
//   const employerId = currentUser?._id;
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
//   const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
//   const [isSalaryHistoryModalOpen, setIsSalaryHistoryModalOpen] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [salaryLoading, setSalaryLoading] = useState({});
//   const [charts, setCharts] = useState({});
//   const [selectedEmployee, setSelectedEmployee] = useState(null);
//   const [editData, setEditData] = useState({});
//   const [payslipData, setPayslipData] = useState({});
//   const [selectedSalaryEmployee, setSelectedSalaryEmployee] = useState(null);
//   const [salaryFormData, setSalaryFormData] = useState({ grossSalary: '', tax: '', penalty: '', hmo: '', other: '' });
//   const [selectedHistoryEmployee, setSelectedHistoryEmployee] = useState(null);
//   const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);

//   useEffect(() => {
//     if (employerId && !isInitialFetchDone) {
//       console.log('Initial fetch triggered for employerId:', employerId, 'status:', status);
//       const userType = currentUser.userType || 'jobEmployer';
//       const id = userType === 'jobEmployer' ? (currentUser.employerId || employerId) : currentUser._id;
//       dispatch(fetchEmployees({ id, userType })).then(() => {
//         setIsInitialFetchDone(true);
//         console.log('Initial fetch completed, status:', status);
//       }).catch((error) => {
//         console.error('Initial fetch failed:', error);
//       });
//     }
//   }, [employerId, currentUser.userType, currentUser._id, currentUser.employerId, dispatch, isInitialFetchDone]);

//   useEffect(() => {
//     if (employees.length > 0) {
//       createCharts();
//     }
//     console.log('Employees updated:', employees);
//   }, [employees]);

//   const handleAddEmployee = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     const formData = { employerId, ...Object.fromEntries(new FormData(e.target)) };
//     try {
//       await dispatch(addEmployee(formData)).unwrap();
//       setIsModalOpen(false);
//       toast.success('Employee added successfully!');
//       dispatch(fetchEmployees({ id: employerId, userType: 'jobEmployer' }));
//     } catch (error) {
//       toast.error('Failed to add employee: ' + error.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAddSalary = async (e) => {
//     e.preventDefault();
//     setSalaryLoading(prev => ({ ...prev, [selectedSalaryEmployee._id]: true }));
//     const formData = { employerId, employeeId: selectedSalaryEmployee._id, ...salaryFormData };
//     try {
//       await dispatch(addSalary(formData)).unwrap();
//       setIsSalaryModalOpen(false);
//       setSalaryFormData({ grossSalary: '', tax: '', penalty: '', hmo: '', other: '' });
//       toast.success('Salary added successfully!');
//       dispatch(fetchEmployees({ id: employerId, userType: 'jobEmployer' }));
//     } catch (error) {
//       toast.error('Failed to add salary: ' + error.message);
//     } finally {
//       setSalaryLoading(prev => ({ ...prev, [selectedSalaryEmployee._id]: false }));
//     }
//   };

//   const handleUpdateEmployee = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     try {
//       await dispatch(updateEmployee({ employerId, employeeId: editData._id, ...editData })).unwrap();
//       setIsEditModalOpen(false);
//       toast.success('Employee updated successfully!');
//       dispatch(fetchEmployees({ id: employerId, userType: 'jobEmployer' }));
//     } catch (error) {
//       toast.error('Failed to update employee: ' + error.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDeleteEmployee = async (employeeId) => {
//     if (window.confirm('Are you sure you want to delete this employee?')) {
//       try {
//         await dispatch(deleteEmployee({ employerId, employeeId })).unwrap();
//         toast.success('Employee deleted successfully!');
//         dispatch(fetchEmployees({ id: employerId, userType: 'jobEmployer' }));
//       } catch (error) {
//         toast.error('Failed to delete employee: ' + error.message);
//       }
//     }
//   };

//   const handleGeneratePayslip = (employee) => {
//     setPayslipData({
//       employee: `${employee.firstName} ${employee.lastName}`,
//       salary: employee.salary.history[employee.salary.history.length - 1] || {},
//     });
//     setIsPayslipModalOpen(true);
//   };

//   const createCharts = () => {
//     const ctxStaff = document.getElementById('staffChart')?.getContext('2d');
//     const ctxSalary = document.getElementById('salaryChart')?.getContext('2d');

//     if (ctxStaff && !charts.staff) {
//       charts.staff = new Chart(ctxStaff, {
//         type: 'pie',
//         data: {
//           labels: employees.map(e => `${e.firstName} ${e.lastName}`),
//           datasets: [{
//             label: 'Employee Distribution',
//             data: employees.map(() => 1),
//             backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//           }],
//         },
//         options: { responsive: true, maintainAspectRatio: false },
//       });
//     }

//     if (ctxSalary && !charts.salary) {
//       const totalSalaries = employees?.map(e => e.salary?.history.reduce((sum, hist) => sum + (hist.netSalary || 0), 0) || 0);
//       charts.salary = new Chart(ctxSalary, {
//         type: 'pie',
//         data: {
//           labels: employees.map(e => `${e.firstName} ${e.lastName}`),
//           datasets: [{
//             label: 'Salary Payments',
//             data: totalSalaries,
//             backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
//           }],
//         },
//         options: { responsive: true, maintainAspectRatio: false },
//       });
//     }
//     setCharts({ ...charts });
//   };

//   const calculateBreakdown = (salary) => {
//     const daily = (salary.netSalary || 0) / 30;
//     const weekly = daily * 7;
//     const monthly = salary.netSalary || 0;
//     return { daily, weekly, monthly };
//   };

//   // Transform employees for EmployerAttendance
//   const attendanceEmployeeList = employees.map(emp => ({
//     id: emp._id,
//     name: `${emp.firstName} ${emp.lastName}` || `Employee ${emp._id.slice(-4)}`,
//   }));

//   return (
//     <div className="min-h-screen bg-gray-100 p-6">
//       <h2 className="text-3xl font-bold text-gray-800 mb-6">Manage Employees</h2>
//       <button
//         onClick={() => setIsModalOpen(true)}
//         className="mb-6 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
//       >
//         <BiPlus /> Add Employee
//       </button>

//       {/* Use the separate EmployerAttendance component */}
//       <EmployerAttendance employeeList={attendanceEmployeeList} />

//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Add New Employee</h3>
//               <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
//                 <BiX size={24} />
//               </button>
//             </div>
//             <form onSubmit={handleAddEmployee} className="space-y-4">
//               <input name="firstName" placeholder="First Name" required className="w-full p-2 border rounded" />
//               <input name="lastName" placeholder="Last Name" required className="w-full p-2 border rounded" />
//               <input name="email" placeholder="Email" required className="w-full p-2 border rounded" />
//               <input name="gender" placeholder="Gender" required className="w-full p-2 border rounded" />
//               <input name="middleName" placeholder="Middle Name" className="w-full p-2 border rounded" />
//               <input name="dateOfBirth" placeholder="Date of Birth" className="w-full p-2 border rounded" />
//               <input name="address" placeholder="Address" className="w-full p-2 border rounded" />
//               <input name="phoneNumber" placeholder="Phone Number" className="w-full p-2 border rounded" />
//               <input name="whatsappNumber" placeholder="WhatsApp Number" className="w-full p-2 border rounded" />
//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
//               >
//                 {loading ? (
//                   <>
//                     <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
//                     Adding...
//                   </>
//                 ) : 'Add Employee'}
//               </button>
//             </form>
//           </div>
//         </div>
//       )}

//       {isEditModalOpen && selectedEmployee && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Edit Employee</h3>
//               <button onClick={() => setIsEditModalOpen(false)} className="text-gray-500 hover:text-gray-700">
//                 <BiX size={24} />
//               </button>
//             </div>
//             <form onSubmit={handleUpdateEmployee} className="space-y-4">
//               <input name="firstName" value={editData.firstName || ''} onChange={(e) => setEditData({ ...editData, firstName: e.target.value })} required className="w-full p-2 border rounded" />
//               <input name="lastName" value={editData.lastName || ''} onChange={(e) => setEditData({ ...editData, lastName: e.target.value })} required className="w-full p-2 border rounded" />
//               <input name="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} required className="w-full p-2 border rounded" />
//               <input name="gender" value={editData.gender || ''} onChange={(e) => setEditData({ ...editData, gender: e.target.value })} required className="w-full p-2 border rounded" />
//               <input name="middleName" value={editData.middleName || ''} onChange={(e) => setEditData({ ...editData, middleName: e.target.value })} className="w-full p-2 border rounded" />
//               <input name="dateOfBirth" value={editData.dateOfBirth || ''} onChange={(e) => setEditData({ ...editData, dateOfBirth: e.target.value })} className="w-full p-2 border rounded" />
//               <input name="address" value={editData.address || ''} onChange={(e) => setEditData({ ...editData, address: e.target.value })} className="w-full p-2 border rounded" />
//               <input name="phoneNumber" value={editData.phoneNumber || ''} onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })} className="w-full p-2 border rounded" />
//               <input name="whatsappNumber" value={editData.whatsappNumber || ''} onChange={(e) => setEditData({ ...editData, whatsappNumber: e.target.value })} className="w-full p-2 border rounded" />
//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-2"
//               >
//                 {loading ? (
//                   <>
//                     <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
//                     Updating...
//                   </>
//                 ) : 'Update Employee'}
//               </button>
//             </form>
//           </div>
//         </div>
//       )}

//       {isPayslipModalOpen && payslipData.employee && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Payslip</h3>
//               <button onClick={() => setIsPayslipModalOpen(false)} className="text-gray-500 hover:text-gray-700">
//                 <BiX size={24} />
//               </button>
//             </div>
//             <div className="space-y-4">
//               <p><strong>Employee:</strong> {payslipData.employee}</p>
//               <p><strong>Gross Salary:</strong> N{payslipData.salary.grossSalary || 0}</p>
//               <p><strong>Deductions:</strong> N{Object.values(payslipData.salary.deductions || {}).reduce((a, b) => a + b, 0)}</p>
//               <p><strong>Net Salary:</strong> N{payslipData.salary.netSalary || 0}</p>
//               <div>
//                 <strong>Breakdown:</strong>
//                 <table className="w-full mt-2">
//                   <thead className="bg-gray-100">
//                     <tr><th className="py-1 px-2">Type</th><th className="py-1 px-2">Amount</th></tr>
//                   </thead>
//                   <tbody>
//                     {Object.entries(calculateBreakdown(payslipData.salary)).map(([type, amount]) => (
//                       <tr key={type} className="border-t">
//                         <td className="py-1 px-2 capitalize">{type}</td>
//                         <td className="py-1 px-2">${amount.toFixed(2)}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//               <button
//                 onClick={() => {
//                   const element = document.createElement('a');
//                   const file = new Blob([`Payslip for ${payslipData.employee}\nGross: $${payslipData.salary.grossSalary || 0}\nDeductions: $${Object.values(payslipData.salary.deductions || {}).reduce((a, b) => a + b, 0)}\nNet: $${payslipData.salary.netSalary || 0}`], { type: 'text/plain' });
//                   element.href = URL.createObjectURL(file);
//                   element.download = `payslip_${payslipData.employee.replace(/ /g, '_')}.txt`;
//                   document.body.appendChild(element);
//                   element.click();
//                   document.body.removeChild(element);
//                 }}
//                 className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
//               >
//                 <BiDownload /> Download Payslip
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {isSalaryModalOpen && selectedSalaryEmployee && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Add Salary for {selectedSalaryEmployee.firstName} {selectedSalaryEmployee.lastName}</h3>
//               <button onClick={() => setIsSalaryModalOpen(false)} className="text-gray-500 hover:text-gray-700">
//                 <BiX size={24} />
//               </button>
//             </div>
//             <form onSubmit={handleAddSalary} className="space-y-4">
//               <input
//                 name="grossSalary"
//                 value={salaryFormData.grossSalary}
//                 onChange={(e) => setSalaryFormData({ ...salaryFormData, grossSalary: e.target.value })}
//                 placeholder="Gross Salary"
//                 type="number"
//                 required
//                 className="w-full p-2 border rounded"
//               />
//               <input
//                 name="tax"
//                 value={salaryFormData.tax}
//                 onChange={(e) => setSalaryFormData({ ...salaryFormData, tax: e.target.value })}
//                 placeholder="Tax"
//                 type="number"
//                 className="w-full p-2 border rounded"
//               />
//               <input
//                 name="penalty"
//                 value={salaryFormData.penalty}
//                 onChange={(e) => setSalaryFormData({ ...salaryFormData, penalty: e.target.value })}
//                 placeholder="Penalty"
//                 type="number"
//                 className="w-full p-2 border rounded"
//               />
//               <input
//                 name="hmo"
//                 value={salaryFormData.hmo}
//                 onChange={(e) => setSalaryFormData({ ...salaryFormData, hmo: e.target.value })}
//                 placeholder="HMO"
//                 type="number"
//                 className="w-full p-2 border rounded"
//               />
//               <input
//                 name="other"
//                 value={salaryFormData.other}
//                 onChange={(e) => setSalaryFormData({ ...salaryFormData, other: e.target.value })}
//                 placeholder="Other Deductions"
//                 type="number"
//                 className="w-full p-2 border rounded"
//               />
//               <button
//                 type="submit"
//                 disabled={salaryLoading[selectedSalaryEmployee._id]}
//                 className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
//               >
//                 {salaryLoading[selectedSalaryEmployee._id] ? (
//                   <>
//                     <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
//                     Adding...
//                   </>
//                 ) : 'Add Salary'}
//               </button>
//             </form>
//           </div>
//         </div>
//       )}

//       {isSalaryHistoryModalOpen && selectedHistoryEmployee && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold">Salary History & Breakdown for {selectedHistoryEmployee.firstName} {selectedHistoryEmployee.lastName}</h3>
//               <button onClick={() => setIsSalaryHistoryModalOpen(false)} className="text-gray-500 hover:text-gray-700">
//                 <BiX size={24} />
//               </button>
//             </div>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <table className="w-full text-sm md:text-base">
//                 <thead className="bg-gray-100"><tr><th className="py-2 px-4">Period</th><th className="py-2 px-4">Gross</th><th className="py-2 px-4">Deductions</th><th className="py-2 px-4">Net</th></tr></thead>
//                 <tbody>
//                   {selectedHistoryEmployee.salary.history.map((hist, i) => (
//                     <tr key={i} className="border-t">
//                       <td className="py-2 px-4">{hist.month} {hist.year}</td>
//                       <td className="py-2 px-4">N{hist.grossSalary.toFixed(2)}</td>
//                       <td className="py-2 px-4">N{Object.values(hist.deductions).reduce((a, b) => a + b, 0).toFixed(2)}</td>
//                       <td className="py-2 px-4">N{hist.netSalary.toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//               <table className="w-full text-sm md:text-base">
//                 <thead className="bg-gray-100"><tr><th className="py-2 px-4">Type</th><th className="py-2 px-4">Amount</th></tr></thead>
//                 <tbody>
//                   {Object.entries(calculateBreakdown(selectedHistoryEmployee.salary.history[selectedHistoryEmployee.salary.history.length - 1] || {})).map(([type, amount]) => (
//                     <tr key={type} className="border-t">
//                       <td className="py-2 px-4 capitalize">{type}</td>
//                       <td className="py-2 px-4">N{amount.toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
//         <div className="bg-white p-4 rounded-lg shadow-lg">
//           <h3 className="text-lg font-semibold mb-2">Employee Distribution</h3>
//           <div className="h-64">
//             <canvas id="staffChart"></canvas>
//           </div>
//         </div>
//         <div className="bg-white p-4 rounded-lg shadow-lg">
//           <h3 className="text-lg font-semibold mb-2">Salary Payments</h3>
//           <div className="h-64">
//             <canvas id="salaryChart"></canvas>
//           </div>
//         </div>
//       </div>

//       <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
//         <h3 className="text-xl font-semibold mb-4">Employed workers & Salaries</h3>
//         <table className="w-full text-sm md:text-base">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="py-2 px-4 text-left">Name</th>
//               <th className="py-2 px-4 text-left">Email</th>
//               <th className="py-2 px-4 text-left">Latest Net Salary</th>
//               <th className="py-2 px-4 text-left">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {employees.map(employee => (
//               <tr key={employee._id} className="border-t">
//                 <td className="py-2 px-4">{employee.firstName} {employee.lastName}</td>
//                 <td className="py-2 px-4">{employee.email}</td>
//                 <td className="py-2 px-4">N{(employee.salary?.history[employee.salary?.history.length - 1]?.netSalary || 0).toFixed(2)}</td>
//                 <td className="py-2 px-4">
//                   <button onClick={() => { setSelectedEmployee(employee); setEditData(employee); setIsEditModalOpen(true); }} className="text-blue-600 hover:text-blue-800 mr-2"><BiEdit /></button>
//                   <button onClick={() => handleDeleteEmployee(employee._id)} className="text-red-600 hover:text-red-800"><BiTrash /></button>
//                   <button onClick={() => handleGeneratePayslip(employee)} className="text-green-600 hover:text-green-800 ml-2"><BiDownload /></button>
//                   <button onClick={() => { setSelectedSalaryEmployee(employee); setIsSalaryModalOpen(true); }} className="text-purple-600 hover:text-purple-800 ml-2"><BiDollar /></button>
//                   <button onClick={() => { setSelectedHistoryEmployee(employee); setIsSalaryHistoryModalOpen(true); }} className="text-teal-600 hover:text-teal-800 ml-2"><BiSearch /></button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {employees.map(employee => (
//         <div key={employee._id} className="bg-white p-6 rounded-lg shadow-lg mb-6">
//           <h3 className="text-xl font-semibold mb-4">{employee.firstName} {employee.lastName} - Salary Breakdown</h3>
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//             <table className="w-full text-sm md:text-base">
//               <thead className="bg-gray-100"><tr><th className="py-2 px-4">Period</th><th className="py-2 px-4">Gross</th><th className="py-2 px-4">Deductions</th><th className="py-2 px-4">Net</th></tr></thead>
//               <tbody>
//                 {employee.salary?.history.map((hist, i) => (
//                   <tr key={i} className="border-t">
//                     <td className="py-2 px-4">{hist.month} {hist.year}</td>
//                     <td className="py-2 px-4">N{hist.grossSalary.toFixed(2)}</td>
//                     <td className="py-2 px-4">N{Object.values(hist.deductions).reduce((a, b) => a + b, 0).toFixed(2)}</td>
//                     <td className="py-2 px-4">N{hist.netSalary.toFixed(2)}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//             <table className="w-full text-sm md:text-base">
//               <thead className="bg-gray-100"><tr><th className="py-2 px-4">Type</th><th className="py-2 px-4">Amount</th></tr></thead>
//               <tbody>
//                 {Object.entries(calculateBreakdown(employee.salary?.history[employee.salary?.history.length - 1] || {})).map(([type, amount]) => (
//                   <tr key={type} className="border-t">
//                     <td className="py-2 px-4 capitalize">{type}</td>
//                     <td className="py-2 px-4">N{amount.toFixed(2)}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//           <h4 className="text-lg font-semibold mb-2">Leave History</h4>
//           <table className="w-full text-sm md:text-base">
//             <thead className="bg-gray-100">
//               <tr>
//                 <th className="py-2 px-4 text-left">Start Date</th>
//                 <th className="py-2 px-4 text-left">End Date</th>
//                 <th className="py-2 px-4 text-left">Reason</th>
//                 <th className="py-2 px-4 text-left">Status</th>
//                 <th className="py-2 px-4 text-left">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {employee.leaveRequests?.map(leave => (
//                 <tr key={leave._id} className="border-t">
//                   <td className="py-2 px-4">{new Date(leave.startDate).toLocaleDateString()}</td>
//                   <td className="py-2 px-4">{new Date(leave.endDate).toLocaleDateString()}</td>
//                   <td className="py-2 px-4">{leave.reason}</td>
//                   <td className="py-2 px-4">{leave.status}</td>
//                   <td className="py-2 px-4">
//                     {leave.status === 'Pending' && (
//                       <>
//                         <button onClick={() => dispatch(approveLeave({ employerId, employeeId: employee._id, leaveId: leave._id })).unwrap().then(() => toast.success('Leave approved!'))} className="text-green-600 hover:text-green-800 mr-2"><BiCheck /></button>
//                         <button onClick={() => dispatch(rejectLeave({ employerId, employeeId: employee._id, leaveId: leave._id })).unwrap().then(() => toast.success('Leave rejected!'))} className="text-red-600 hover:text-red-800"><BiX /></button>
//                       </>
//                     )}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       ))}
//     </div>
//   );
// };

// export default EmployerEmployee;

// const calculateBreakdown = (salary) => {
//   const daily = (salary.netSalary || 0) / 30;
//   const weekly = daily * 7;
//   const monthly = salary.netSalary || 0;
//   return { daily, weekly, monthly };
// };
